import {
  BufferAttribute,
  type Raycaster,
  type SkinnedMesh,
  Triangle,
  Vector3,
} from 'three';

import type { KeyPoseFkJointId } from '../types';
import { posePickMetaFromSkinWeights } from './poseMeshBoneResolver';
import type { PoseMeshPickEntry } from './poseMeshPickEntry';
import type { MotionEditorBonePickerSkeletonType } from './types';

interface PoseMeshSurfaceTriangle {
  vertexIndices: [number, number, number];
}

export interface PoseMeshSurfacePickEntry {
  mesh: SkinnedMesh;
  trianglesByJointId: Map<KeyPoseFkJointId, PoseMeshSurfaceTriangle[]>;
}

export interface PoseMeshSurfaceIntersection {
  distance: number;
  jointId: KeyPoseFkJointId;
  meshName: string;
  point: Vector3;
  sourceBoneName: string;
}

const scratch = {
  a: new Vector3(),
  b: new Vector3(),
  c: new Vector3(),
  hit: new Vector3(),
  barycentric: new Vector3(),
};

export function buildPoseMeshSurfacePickEntries(
  entries: readonly PoseMeshPickEntry[],
) {
  const surfaceEntries: PoseMeshSurfacePickEntry[] = [];

  for (const entry of entries) {
    const trianglesByJointId = buildTrianglesByJointId(entry);
    if (trianglesByJointId.size === 0) continue;

    surfaceEntries.push({ mesh: entry.mesh, trianglesByJointId });
  }

  return surfaceEntries;
}

export function intersectPoseMeshSurfacePickEntries(
  entries: readonly PoseMeshSurfacePickEntry[],
  raycaster: Raycaster,
  candidateJointIds: readonly KeyPoseFkJointId[],
  skeletonType: MotionEditorBonePickerSkeletonType,
) {
  let best: PoseMeshSurfaceIntersection | null = null;

  for (const entry of entries) {
    const mesh = entry.mesh;
    if (!isObjectVisible(mesh)) continue;

    const position = mesh.geometry.getAttribute('position') as
      | BufferAttribute
      | undefined;
    if (!position?.count) continue;

    mesh.skeleton?.update?.();
    mesh.updateMatrixWorld(true);

    for (const candidateJointId of candidateJointIds) {
      const triangles = entry.trianglesByJointId.get(candidateJointId);
      if (!triangles?.length) continue;

      const hit = intersectPoseMeshSurfaceTriangles({
        candidateJointId,
        mesh,
        position,
        raycaster,
        skeletonType,
        triangles,
      });
      if (hit && (!best || hit.distance < best.distance)) {
        best = hit;
      }
    }
  }

  return best;
}

function buildTrianglesByJointId(entry: PoseMeshPickEntry) {
  const position = entry.mesh.geometry.getAttribute('position');
  if (!(position instanceof BufferAttribute)) {
    return new Map<KeyPoseFkJointId, PoseMeshSurfaceTriangle[]>();
  }

  const jointIdsByVertex = buildJointIdsByVertex(entry.vertexGroups);
  const index = entry.mesh.geometry.index;
  const triangleCount = index
    ? Math.floor(index.count / 3)
    : Math.floor(position.count / 3);
  const trianglesByJointId = new Map<
    KeyPoseFkJointId,
    PoseMeshSurfaceTriangle[]
  >();

  for (let triangle = 0; triangle < triangleCount; triangle += 1) {
    const offset = triangle * 3;
    const ia = index ? index.getX(offset) : offset;
    const ib = index ? index.getX(offset + 1) : offset + 1;
    const ic = index ? index.getX(offset + 2) : offset + 2;
    const jointIds = getTriangleJointIds(jointIdsByVertex, ia, ib, ic);
    if (jointIds.length === 0) continue;

    const surfaceTriangle = {
      vertexIndices: [ia, ib, ic],
    } satisfies PoseMeshSurfaceTriangle;
    for (const jointId of jointIds) {
      const triangles = trianglesByJointId.get(jointId) ?? [];
      triangles.push(surfaceTriangle);
      trianglesByJointId.set(jointId, triangles);
    }
  }

  return trianglesByJointId;
}

function buildJointIdsByVertex(
  vertexGroups: ReadonlyMap<KeyPoseFkJointId, readonly number[]>,
) {
  const jointIdsByVertex = new Map<number, KeyPoseFkJointId[]>();

  for (const [jointId, vertexIndices] of vertexGroups) {
    for (const vertexIndex of vertexIndices) {
      const jointIds = jointIdsByVertex.get(vertexIndex) ?? [];
      jointIds.push(jointId);
      jointIdsByVertex.set(vertexIndex, jointIds);
    }
  }

  return jointIdsByVertex;
}

function getTriangleJointIds(
  jointIdsByVertex: ReadonlyMap<number, readonly KeyPoseFkJointId[]>,
  ia: number,
  ib: number,
  ic: number,
) {
  const jointIds: KeyPoseFkJointId[] = [];
  addUniqueJointIds(jointIds, jointIdsByVertex.get(ia));
  addUniqueJointIds(jointIds, jointIdsByVertex.get(ib));
  addUniqueJointIds(jointIds, jointIdsByVertex.get(ic));

  return jointIds;
}

function addUniqueJointIds(
  target: KeyPoseFkJointId[],
  source: readonly KeyPoseFkJointId[] | undefined,
) {
  if (!source) return;

  for (const jointId of source) {
    if (!target.includes(jointId)) {
      target.push(jointId);
    }
  }
}

function intersectPoseMeshSurfaceTriangles({
  candidateJointId,
  mesh,
  position,
  raycaster,
  skeletonType,
  triangles,
}: {
  candidateJointId: KeyPoseFkJointId;
  mesh: SkinnedMesh;
  position: BufferAttribute;
  raycaster: Raycaster;
  skeletonType: MotionEditorBonePickerSkeletonType;
  triangles: readonly PoseMeshSurfaceTriangle[];
}) {
  let best: PoseMeshSurfaceIntersection | null = null;

  for (const triangle of triangles) {
    const [ia, ib, ic] = triangle.vertexIndices;

    posePickWorldVertex(mesh, position, ia, scratch.a);
    posePickWorldVertex(mesh, position, ib, scratch.b);
    posePickWorldVertex(mesh, position, ic, scratch.c);

    const point = raycaster.ray.intersectTriangle(
      scratch.a,
      scratch.b,
      scratch.c,
      false,
      scratch.hit,
    );
    if (!point) continue;

    const distance = raycaster.ray.origin.distanceTo(point);
    if (distance < raycaster.near || distance > raycaster.far) continue;
    if (best && distance >= best.distance) continue;

    Triangle.getBarycoord(
      point,
      scratch.a,
      scratch.b,
      scratch.c,
      scratch.barycentric,
    );
    const meta = posePickMetaFromSkinWeights({
      barycentricWeights: [
        scratch.barycentric.x,
        scratch.barycentric.y,
        scratch.barycentric.z,
      ],
      mesh,
      skeletonType,
      vertexIndices: triangle.vertexIndices,
    });

    best = {
      distance,
      jointId: meta?.jointId ?? candidateJointId,
      meshName: mesh.name,
      point: point.clone(),
      sourceBoneName: meta?.sourceBoneName ?? candidateJointId,
    };
  }

  return best;
}

function posePickWorldVertex(
  mesh: SkinnedMesh,
  position: BufferAttribute,
  index: number,
  target: Vector3,
) {
  const meshWithVertexPosition = mesh as SkinnedMesh & {
    getVertexPosition?: (index: number, target: Vector3) => Vector3;
  };

  if (typeof meshWithVertexPosition.getVertexPosition === 'function') {
    meshWithVertexPosition.getVertexPosition(index, target);
  } else {
    target.fromBufferAttribute(position, index);
    mesh.applyBoneTransform(index, target);
  }

  return target.applyMatrix4(mesh.matrixWorld);
}

function isObjectVisible(object: SkinnedMesh) {
  let current: SkinnedMesh | null = object;
  while (current) {
    if (!current.visible) return false;
    current = current.parent as SkinnedMesh | null;
  }

  return true;
}
